"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const baseData_1 = require("@powerednow/complexData/baseData");
const use_async_effect_1 = require("use-async-effect");
const react_error_boundary_1 = require("react-error-boundary");
const awesome_debounce_promise_1 = require("awesome-debounce-promise");
const modelLookup_1 = require("@powerednow/models/modelLookup");
const cleanupListeners = removers => {
    removers?.list.forEach(cleaner => cleaner());
    removers?.list.splice(0, removers.length);
};
const setMonitoredComplexDataInstance = (listenerRemovers, complexData, monitoredInstances, eventMap) => {
    if (complexData && !monitoredInstances.has(complexData)) {
        Object.entries(eventMap).forEach(([event, changeAction]) => {
            complexData.on(event, changeAction);
            listenerRemovers.list.push(() => {
                complexData.removeListener(event, changeAction);
            });
        });
        monitoredInstances.add(complexData);
    }
};
function useComplexData(complexModelName, deps, dataWrapperInstance, callback = async () => {
}, handleError = (0, react_error_boundary_1.useErrorHandler)()) {
    if (!dataWrapperInstance) {
        throw new Error('You must use `useComplexData` in DataWrapperContext, or pass a dataWrapper instance');
    }
    const processedDeps = Array.isArray(deps) ? deps : [deps];
    const [details, setDetails] = (0, react_1.useState)();
    const [optionalResult, setResult] = (0, react_1.useState)();
    const [error] = (0, react_1.useState)();
    const debouncedCallback = (0, awesome_debounce_promise_1.default)(callback, 500);
    const listenerRemovers = { list: [] };
    const getData = async (isMounted) => {
        const monitoredComplexInstances = new Set();
        const cleanupIfUnmounted = () => {
            if (!isMounted()) {
                cleanupListeners(listenerRemovers);
                return true;
            }
            return false;
        };
        try {
            const callbackResultSetter = (newResult) => {
                if (cleanupIfUnmounted()) {
                    return;
                }
                setResult({ ...optionalResult, ...newResult });
            };
            const complexData = await dataWrapperInstance.getComplexDataObject((0, modelLookup_1.complexFindModel)(complexModelName), processedDeps[0]);
            const watcher = async (itemsToWatch) => {
                const getterResult = await itemsToWatch;
                (Array.isArray(getterResult) ? getterResult : [getterResult]).forEach(complexDataItem => {
                    setMonitoredComplexDataInstance(listenerRemovers, complexDataItem, monitoredComplexInstances, {
                        [baseData_1.default.EVENTS.FIELD_CHANGED]: ({ eventId, item }) => {
                            if (cleanupIfUnmounted()) {
                                return;
                            }
                            if (item === complexDataItem) {
                                setDetails(complexDataItem.data.getPureDataValues());
                            }
                        },
                        [baseData_1.default.EVENTS.OWN_ASSOCIATION_ADDED]: ({ eventId, item }) => {
                            if (cleanupIfUnmounted()) {
                                return;
                            }
                            if (item === complexDataItem) {
                                debouncedCallback(complexData, callbackResultSetter, watcher).catch(handleError);
                            }
                        },
                        [baseData_1.default.EVENTS.OWN_ASSOCIATION_ID_UPDATED]: ({ eventId, _item }) => {
                            if (cleanupIfUnmounted()) {
                                return;
                            }
                            debouncedCallback(complexData, callbackResultSetter, watcher).catch(handleError);
                        },
                        [baseData_1.default.EVENTS.OWN_ASSOCIATION_SAVED]: ({ eventId, _item }) => {
                            if (cleanupIfUnmounted()) {
                                return;
                            }
                            debouncedCallback(complexData, callbackResultSetter, watcher).catch(handleError);
                        },
                    });
                });
                return getterResult;
            };
            if (cleanupIfUnmounted()) {
                return;
            }
            await watcher(Promise.resolve(complexData));
            if (cleanupIfUnmounted()) {
                return;
            }
            // @ts-ignore
            setDetails(complexData.data.getPureDataValues());
            if (cleanupIfUnmounted()) {
                return;
            }
            await debouncedCallback(complexData, callbackResultSetter, watcher).catch(handleError);
            cleanupIfUnmounted();
        }
        catch (err) {
            if (cleanupIfUnmounted()) {
                return;
            }
            handleError(err);
        }
    };
    (0, use_async_effect_1.useAsyncEffect)(async (isMounted) => {
        if (processedDeps[0]) {
            return getData(isMounted);
        }
        return null;
    }, () => cleanupListeners(listenerRemovers), processedDeps);
    return {
        details,
        optionalResult,
        error,
    };
}
exports.default = useComplexData;
//# sourceMappingURL=../../src/complexDataHook.js.map